import { SearchInput } from "@flexfreight/ui";
import { useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from "react-router";
import { useConsoleMe } from "../../auth/context";
import { navFor } from "../../shell/nav";
import { INTEGRATION_CATEGORIES, INTEGRATIONS, SETTING_GROUPS } from "./settingsCatalog";

/** One searchable entry: every settings link and every integration (its pop-up), filtered by capability. */
type Hit = { label: string; hint: string; to: string; words: string };

function useSettingsIndex(): Hit[] {
  const me = useConsoleMe();
  return useMemo(() => {
    const have = new Set(me.capabilities);
    const seen = new Set<string>();
    const out: Hit[] = [];
    const add = (h: Hit) => {
      if (seen.has(h.to)) return;
      seen.add(h.to);
      out.push({ ...h, words: h.words.toLowerCase() });
    };
    for (const g of SETTING_GROUPS) for (const l of g.links) if (have.has(l.cap)) add({ label: l.label, hint: g.label, to: l.to, words: `${l.label} ${l.keywords ?? ""} ${g.label}` });
    if (have.has("integrations:manage")) for (const i of INTEGRATIONS) add({ label: i.name, hint: `Integrations · ${i.category}`, to: `/admin/integrations?open=${i.id}`, words: `${i.name} ${i.blurb} ${i.category} ${i.keywords}` });
    return out;
  }, [me.capabilities]);
}


/** "Search settings" for the whole Admin Center: type, pick a result (arrows + Enter), land on the setting. */
export function SettingsSearch({ className, autoFocus }: { className?: string; autoFocus?: boolean }) {
  const index = useSettingsIndex();
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const [cursor, setCursor] = useState(0);
  const t = term.trim().toLowerCase();
  const hits = t ? index.filter((h) => t.split(/\s+/).every((w) => h.words.includes(w))).slice(0, 8) : [];
  const listId = "ffa-setsearch-list";
  const box = useRef<HTMLDivElement>(null);
  const go = (h: Hit) => {
    setTerm("");
    navigate(h.to);
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!hits.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(hits.length - 1, c + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(hits[Math.min(cursor, hits.length - 1)]!);
    } else if (e.key === "Escape") setTerm("");
  };
  return (
    <div className={`ffa-setsearch ${className ?? ""}`} ref={box} onKeyDown={onKey}>
      <SearchInput
        label="Search settings"
        placeholder="Search settings: carriers, Zoho, two-step, team, invoices..."
        value={term}
        autoFocus={autoFocus}
        onChange={(v) => {
          setTerm(v);
          setCursor(0);
        }}
        aria-controls={listId}
        aria-expanded={hits.length > 0}
        role="combobox"
        aria-autocomplete="list"
      />
      {t ? (
        <ul id={listId} className="ffa-setsearch__list" role="listbox" aria-label="Matching settings">
          {hits.length ? (
            hits.map((h, i) => (
              <li key={h.to} role="option" aria-selected={i === cursor}>
                <Link to={h.to} className="ffa-setsearch__hit" data-active={i === cursor || undefined} onClick={() => setTerm("")} onMouseEnter={() => setCursor(i)}>
                  <span className="ffa-setsearch__label">{h.label}</span>
                  <span className="ffa-setsearch__hint">{h.hint}</span>
                </Link>
              </li>
            ))
          ) : (
            <li className="ffa-setsearch__none">No setting matches &ldquo;{term.trim()}&rdquo;.</li>
          )}
        </ul>
      ) : null}
    </div>
  );
}

/* Generic line glyphs for the sub-menu (not brand marks). */
const ICON: Record<string, ReactNode> = {
  "/admin": <path d="M3 3h6v6H3zM11 3h6v6h-6zM3 11h6v6H3zM11 11h6v6h-6z" />,
  "/admin/carriers": <path d="M2 6h10v8H2zM12 9h3.5L18 11.5V14h-6M5 16.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM14.5 16.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" />,
  "/admin/integrations": <path d="M7 2v4M13 2v4M5 6h10v3a5 5 0 0 1-10 0zM10 14v4" />,
  "/admin/team": <path d="M7 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM2 17c0-3 2.2-5 5-5s5 2 5 5M14 9a2.5 2.5 0 1 0 0-5M15 12c2 .4 3 2.2 3 5" />,
  "/admin/company": <path d="M3 17V4h9v13M12 8h5v9M6 7h3M6 10h3M6 13h3M1 17h18" />,
  "/admin/audit": <path d="M5 2h8l3 3v13H5zM8 8h5M8 11h5M8 14h3" />,
  "/admin/problems": <path d="M4 18V3M4 3h10l-2 4 2 4H4" />,
  "/admin/health": <path d="M1 10h4l2-5 3 10 3-7 2 2h4" />,
};

function Glyph({ to }: { to: string }) {
  return (
    <svg className="ffa-subnav__icon" viewBox="0 0 20 20" aria-hidden="true">
      {ICON[to] ?? <circle cx="10" cy="10" r="6" />}
    </svg>
  );
}

/**
 * The Admin Center frame, the same on every admin page (Empire layout): breadcrumb, one settings search, a dark
 * sub-menu styled like the main menu, and the page beside it. Integrations lists its categories under it.
 */
export function AdminFrame({ sub, children }: { sub?: string; children: ReactNode }) {
  const me = useConsoleMe();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const items = navFor(me.capabilities).find((g) => g.id === "admin")?.items ?? [];
  const here = items.filter((i) => i.to !== "/admin").find((i) => pathname === i.to || pathname.startsWith(`${i.to}/`));
  const onIntegrations = pathname === "/admin/integrations";
  const cat = params.get("cat");
  return (
    <div className="ffa-adminframe">
      <div className="ffa-adminframe__top">
        <nav className="ffa-crumbs" aria-label="Breadcrumb">
          <Link to="/admin" className="ffa-crumbs__back">All settings</Link>
          {here ? (
            <>
              <span aria-hidden="true">/</span>
              {sub ? <Link to={here.to} className="ffa-crumbs__link">{here.label}</Link> : <span aria-current="page" className="ffa-crumbs__here">{here.label}</span>}
            </>
          ) : null}
          {sub ? (
            <>
              <span aria-hidden="true">/</span>
              <span aria-current="page" className="ffa-crumbs__here">{sub}</span>
            </>
          ) : null}
        </nav>
        <SettingsSearch className="ffa-adminframe__search" />
      </div>
      <div className="ffa-adminframe__body">
        <aside className="ffa-subnav" aria-label="Admin Center">
          <p className="ffa-subnav__label">Admin Center</p>
          <ul>
            {items.map((i) => (
              <li key={i.to}>
                <NavLink to={i.to} end={i.to === "/admin" || i.to === "/admin/integrations" ? true : undefined} className="ffa-subnav__item">
                  <Glyph to={i.to} />
                  <span>{i.label}</span>
                </NavLink>
                {i.to === "/admin/integrations" && onIntegrations ? (
                  <ul className="ffa-subnav__children" aria-label="Integration categories">
                    <li>
                      <Link to="/admin/integrations" className="ffa-subnav__child" aria-current={!cat ? "true" : undefined}>All integrations</Link>
                    </li>
                    {INTEGRATION_CATEGORIES.map((c) => (
                      <li key={c}>
                        <Link to={`/admin/integrations?cat=${encodeURIComponent(c)}`} className="ffa-subnav__child" aria-current={cat === c ? "true" : undefined}>{c}</Link>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        </aside>
        <div className="ffa-adminframe__main">{children}</div>
      </div>
    </div>
  );
}
